import {
  DepositCase,
  DepositCaseFilter,
  GeneralTenantCase,
  GeneralTenantCaseFilter,
  MietendeckelCase,
  MietendeckelCaseFilter,
  MietminderungCase,
  MietminderungCaseFilter,
  MietpreisbremseCase,
  MietpreisbremseCaseFilter,
  OperatingCostCase,
  OperatingCostCaseFilter,
  Profile,
  ProfileUpdate,
} from "@happy-legal/graphql-types";
import { cloneDeep, merge } from "lodash";
import { graphQlClient } from "../clients";
import { MUTATION_UPDATE_PROFILE } from "../gql/mutations";
import { MUTATION_UPDATE_USER } from "../gql/mutations/user";
import { caseInitState } from "../providers/CaseProvider";
import { removeNulls } from "../utils";
import { handleCatchReducer } from "./handleCatchReducer";

type TCaseFilter =
  | MietendeckelCaseFilter
  | MietminderungCaseFilter
  | MietpreisbremseCaseFilter
  | GeneralTenantCaseFilter
  | OperatingCostCaseFilter
  | DepositCaseFilter;

export const GET_ALL_CASES = "GET_ALL_CASES";
type TGET_ALL_CASES = {
  type: "GET_ALL_CASES";
  query: string;
  filter?: TCaseFilter;
  notEqual?: TCaseFilter;
  limit?: number;
  offset?: number;
};

export const GET_CASE = "GET_CASE";
type TGET_CASE = {
  type: "GET_CASE";
  query: string;
  id: string;
};

export const GET_MERGE_CASE = "GET_MERGE_CASE";
type TGET_MERGE_CASE = {
  type: "GET_MERGE_CASE";
  query: string;
  variables?: any;
};

export const ADD_CASE = "ADD_CASE";
type TADD_CASE = {
  type: "ADD_CASE";
  mutation: string;
  payload: { [key: string]: any };
};

export const UPDATE_CASE = "UPDATE_CASE";
export type TUPDATE_CASE = {
  type: "UPDATE_CASE";
  mutation: string;
  payload: any;
  isFile?: boolean;
};

export const DELETE_CASE = "DELETE_CASE";
type TDELETE_CASE = {
  type: "DELETE_CASE";
  mutation: string;
  id: string;
};

export const UPDATE_OUTSIDE_CASE = "UPDATE_OUTSIDE_CASE";
type TUPDATE_OUTSIDE_CASE = {
  type: "UPDATE_OUTSIDE_CASE";
  payload: any;
};

export type TCaseAction =
  | TGET_CASE
  | TGET_MERGE_CASE
  | TGET_ALL_CASES
  | TADD_CASE
  | TUPDATE_CASE
  | TDELETE_CASE
  | TUPDATE_OUTSIDE_CASE;

export type TCase =
  | MietminderungCase
  | MietendeckelCase
  | MietpreisbremseCase
  | GeneralTenantCase
  | OperatingCostCase
  | DepositCase;

export type TCaseState = {
  caseData: TCase;
  allCases: { items: TCase[]; total: number };
};

export const caseReducer = async (state: TCaseState, action: TCaseAction) => {
  try {
    switch (action.type) {
      case GET_ALL_CASES: {
        const { query, ...variables } = action;
        const newAllCases = await graphQlClient(query, variables);

        return { ...state, allCases: newAllCases || [] };
      }

      // NOTE: Can theoretically retrieve this from allCases
      // but may be stored in case state/query field differ
      case GET_CASE: {
        const newCaseData = await graphQlClient(action.query, {
          id: action.id,
        });

        return { ...state, caseData: newCaseData };
      }

      case GET_MERGE_CASE: {
        const { query } = action;

        const newCaseData = await graphQlClient(query).then((res) =>
          merge({}, cloneDeep(state.caseData), res)
        );

        return { ...state, caseData: newCaseData };
      }

      case ADD_CASE: {
        const newCaseData = await graphQlClient(action.mutation, {
          input: action.payload,
        });

        const newState = { ...state, caseData: newCaseData };

        newState.allCases.items = [...state.allCases.items, newCaseData];
        newState.allCases.total = state?.allCases?.total + 1;

        return newState;
      }

      case UPDATE_CASE: {
        //Deep clone, remove nulls and
        const deepClonedPayload = cloneDeep(action.payload);
        const sanitisedPayload = removeNulls(deepClonedPayload);

        // Retrieve input from payload
        const {
          // Removes rentalObject, rentalObjectId and profile
          rentalObject, // eslint-disable-line @typescript-eslint/no-unused-vars
          rentalObjectId, // eslint-disable-line @typescript-eslint/no-unused-vars
          profile, // eslint-disable-line @typescript-eslint/no-unused-vars
          ...input
        } = sanitisedPayload;

        let newCaseData = deepClonedPayload;
        const variables = { id: state.caseData.id, input };

        // Send payload and return case
        newCaseData = await graphQlClient(action.mutation, variables);

        return { ...state, caseData: newCaseData };
      }

      case DELETE_CASE: {
        const { mutation, id } = action;
        await graphQlClient(mutation, { id });

        const newAllCases = {
          items: state?.allCases?.items?.filter((item) => item?.id !== id),
          total: state?.allCases?.total - 1,
        };

        const newCaseData =
          state?.caseData?.id === action.id ? {} : state?.caseData;

        return { allCasesData: newAllCases, caseData: newCaseData };
      }

      case UPDATE_OUTSIDE_CASE: {
        // Retrieve ids
        const caseData = state.caseData;
        const { ownerSub, rentalObjectId } = caseData;

        // Clone and sanitise payload
        const deepClonedPayload = cloneDeep(action.payload);
        const sanitisedPayload = removeNulls(deepClonedPayload);

        // Target input data
        const profile = sanitisedPayload?.profile;
        const rentalObject = sanitisedPayload?.rentalObject;
        const user = sanitisedPayload?.profile?.user;

        if (user && profile) {
          delete profile.user;
        }

        // Helpers
        const hasProfileData = !!(profile && !!Object.keys(profile).length);
        const hasRentalObjectData = !!(
          rentalObject && !!Object.keys(rentalObject)?.length
        );
        const hasUserData = !!(user && !!Object.keys(user)?.length);

        // Clone state so as to not mutate it
        let newCaseDataState = cloneDeep(caseData);

        // If profile relevant data
        if (hasProfileData || hasRentalObjectData) {
          // Create input
          const profileInput = {
            ...(hasProfileData && { profile }),
            ...(hasRentalObjectData && {
              rentalObjects: [{ id: rentalObjectId, ...rentalObject }],
            }),
          };

          const newProfileData: Profile = await graphQlClient(
            MUTATION_UPDATE_PROFILE,
            {
              id: ownerSub,
              input: profileInput as ProfileUpdate,
            }
          );

          // @ts-ignore - backend retrieves rentalObject when returning case/frontend is obj
          newCaseDataState = {
            ...newCaseDataState,
            // Find and update updated rentalObject
            ...(hasRentalObjectData && {
              rentalObject: newProfileData.rentalObjects!.find(
                (rentalObject) => rentalObject.id === rentalObjectId
              ),
            }),
            ...(hasProfileData && { profile: newProfileData }),
          };
        }

        // Update user
        if (hasUserData) {
          (newCaseDataState.profile as any as Profile).user =
            await graphQlClient(MUTATION_UPDATE_USER, {
              sub: ownerSub,
              input: user,
            });
        }

        const newState = { ...state, caseData: newCaseDataState };

        // allCases were already previously loaded, update them too
        const hasAllCases = !!state?.allCases?.items.length;
        if (hasAllCases) {
          const newAllCasesItemsState = state?.allCases?.items?.map(
            (caseData) =>
              caseData?.id === newCaseDataState.id ? newCaseDataState : caseData
          );

          newState.allCases.items = newAllCasesItemsState;
        }

        return newState;
      }

      default: {
        return state;
      }
    }
  } catch (err) {
    handleCatchReducer("case", err);
    return caseInitState;
  }
};
